import { Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import usePanelState from '@databases/panel/usePanelState';
import { getAllPanelsForPanelRenderer } from '@databases/panel-types';
import React, { type FunctionComponent, useCallback, useMemo, useRef } from 'react';
import { type ImperativePanelHandle, Panel } from 'react-resizable-panels';
import styled, { css } from 'styled-components';

import PanelRenderer from './PanelRenderer';
import AccessibleResizeHandle from './accessible-resize-handle';
import PanelSizes from './panel/sizes';
import { createPanelStorageKey, getDefaultPanelSize } from './panel/utils';
import Z_INDEX from './z-index';

const SidebarPanel = styled(Panel)<{ $showTopBorder: boolean }>`
  ${(prop) =>
    prop.$showTopBorder &&
    css`
      border-top: ${token('border.width', '1px')} solid
        ${token('color.border', '#EBECF0')};
      margin-top: ${token('space.100')};
    `}
  z-index: ${Z_INDEX.SIDE_PANEL_CONTAINER};
  min-width: 300px; /* Ensure minimum width in pixels regardless of viewport size */
`;

const componentStyles = xcss({
  width: '100%',
  height: '100%',
  overflow: 'auto',
});

interface SidePanelProps {
  sidebar?: React.ReactNode | null;
  showUpdatedSidePanel: boolean;
  options: {
    toolbarLocation: 'top' | 'views';
  };
  resizeDisabled?: boolean;
  contentId: string;
}

const SidePanel: FunctionComponent<SidePanelProps> = ({
  sidebar,
  options,
  showUpdatedSidePanel,
  resizeDisabled,
  contentId,
}) => {
  const { getPanelKey } = usePanelState();
  const panelKey = getPanelKey();
  const storageKey = useMemo(() => createPanelStorageKey(panelKey), [panelKey]);
  const analyticsClient = useAnalytics();

  const panelRef = useRef<ImperativePanelHandle>(null);

  const handleDragging = useCallback(
    (isDragging: boolean) => {
      if (!isDragging && panelKey && panelRef.current) {
        const panelSize = panelRef.current.getSize();
        analyticsClient.sendPanelResizedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          panelKey,
          panelSize,
        });
      }
    },
    [analyticsClient, contentId, panelKey]
  );

  /**
   * If a sidebar component is passed, that takes priority.
   * If a panel key is set, this defers to the panel component.
   */
  const sidebarComponent = useMemo(() => {
    if (sidebar) {
      return sidebar;
    }

    if (panelKey) {
      const panels = getAllPanelsForPanelRenderer();

      return <PanelRenderer panels={panels} panel={panelKey} />;
    }

    return null;
  }, [panelKey, sidebar]);

  const sidebarPanelProps = useMemo(() => {
    const isDevtools = panelKey === 'DevtoolsPanel';
    const panelType = isDevtools
      ? 'devtools'
      : showUpdatedSidePanel
      ? 'newSidebar'
      : 'sidebar';
    const panelConfig = PanelSizes[panelType];

    return {
      $showTopBorder: !showUpdatedSidePanel && options?.toolbarLocation === 'top',
      order: panelConfig.order,
      maxSize: panelConfig.max,
      minSize: panelConfig.min,
      defaultSize: getDefaultPanelSize(panelType, storageKey),
      'data-testid': showUpdatedSidePanel
        ? 'layout-right-side-panel'
        : 'legacy-layout-right-side-panel',
    };
  }, [showUpdatedSidePanel, storageKey, options?.toolbarLocation, panelKey]);

  const resizeHandleStyle = useMemo(
    () => ({
      // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
      marginTop:
        !showUpdatedSidePanel && options?.toolbarLocation === 'top'
          ? token('space.100')
          : 0,
      marginLeft: 0,
      marginRight: 0,
      marginBottom: 0,
    }),
    [showUpdatedSidePanel, options?.toolbarLocation]
  );

  if (!sidebarComponent) {
    return null;
  }

  return (
    <>
      <AccessibleResizeHandle
        id="app-resize-handle"
        style={resizeHandleStyle}
        disabled={resizeDisabled}
        onDragging={handleDragging}
        panelRef={panelRef}
        minSize={sidebarPanelProps.minSize}
        maxSize={sidebarPanelProps.maxSize}
      />

      <SidebarPanel {...sidebarPanelProps} id="right-side-panel" ref={panelRef}>
        <Flex
          justifyContent="start"
          alignItems="center"
          direction="column"
          xcss={componentStyles}
        >
          {sidebarComponent}
        </Flex>
      </SidebarPanel>
    </>
  );
};

export default SidePanel;
